import {
  ClipboardContentInsertionEvent,
  ClipboardPipeline,
  Editor,
  DowncastConversionApi,
  Element,
  ViewDocumentSelection,
  IconCheck,
  IconEraser,
  ButtonView,
  toWidget,
  WidgetToolbarRepository,
} from "ckeditor5";
import { css } from "styled-components";

import { IconLatexSwitchLine } from "@examspark/common-ui/dist/components/icons";

import { PluginWithStyles } from "../PluginWithStyles";

import { ConvertLatexCommand, RecognizeLatexCommand } from "./commands";

//
// Match $...$ or \[...\]
//
export const LATEX_REGEX = /(\$(?<inline>\S+)\$)|(\\\[(?<block>.+)\\\])/g;

export const UI_BUTTON_RECOGNIZE_LATEX = "recognizeLatex";
export const COMMAND_RECOGNIZE_LATEX = "recognizeLatex";
export const COMMAND_CONVERT_LATEX = "convertLatex";

export default class ConvertLatexToEquationsPlugin extends PluginWithStyles {
  convertCommand: ConvertLatexCommand;
  recognizeCommand: RecognizeLatexCommand;

  public static buildEditorStyles = () => css`
    .latex-inline,
    .latex-block {
      border-radius: 3px;
      border: var(--rce-widget-border);
      padding: 0.25rem;
    }
  `;

  public static get requires() {
    return [WidgetToolbarRepository];
  }

  constructor(editor: Editor) {
    super(editor);

    this.recognizeCommand = new RecognizeLatexCommand(editor);
    this.convertCommand = new ConvertLatexCommand(editor);
  }

  init() {
    this.editor.model.schema.register("latex-inline", {
      allowWhere: "$text",
      allowChildren: "$text",
      isInline: true,
      isContent: true,
    });

    this.editor.model.schema.register("latex-block", {
      allowWhere: ["$block", "$blockObject"],
      allowChildren: "$text",
      isContent: true,
    });

    this.editor.conversion.for("dataDowncast").elementToElement({
      model: "latex-inline",
      view: {
        name: "span",
        classes: "latex-inline",
      },
    });

    this.editor.conversion.for("dataDowncast").elementToElement({
      model: "latex-block",
      view: {
        name: "div",
        classes: "latex-block",
      },
    });

    this.editor.conversion.for("editingDowncast").elementToElement({
      model: "latex-inline",
      view: this.createLatexViewElement,
    });

    this.editor.conversion.for("editingDowncast").elementToElement({
      model: "latex-block",
      view: this.createLatexViewElement,
    });

    this.editor.conversion.for("upcast").elementToElement({
      view: {
        classes: /latex-(inline|block)/,
      },
      model: (element, { writer }) => {
        const [text, ...children] = element.getChildren();
        if (!text?.is("$text") || children.length > 0) {
          return null;
        }

        const match = text.data.match(LATEX_REGEX)?.[0];
        if (!match) return null;

        const type = Array.from(element.getClassNames()).find((c) =>
          c.startsWith("latex-"),
        );

        //
        // Shouldn't be possible given that we matched the view pattern
        // This is just to make typescript happy
        //
        if (type == null) return null;

        return writer.createElement(type, {
          latex: match[1],
        });
      },
    });

    this.editor.commands.add(COMMAND_RECOGNIZE_LATEX, this.recognizeCommand);
    this.editor.commands.add(COMMAND_CONVERT_LATEX, this.convertCommand);

    this.editor.ui.componentFactory.add(UI_BUTTON_RECOGNIZE_LATEX, (locale) => {
      const button = new ButtonView(locale);
      button.set({
        label: this.editor.t("Recognize LaTeX equations"),
        icon: IconLatexSwitchLine.Content,
        tooltip: true,
      });
      button.bind("isEnabled").to(this.recognizeCommand, "isEnabled");
      button.on("execute", () => this.recognizeCommand.execute());
      return button;
    });

    this.editor.plugins
      .get(ClipboardPipeline)
      .on<ClipboardContentInsertionEvent>(
        "contentInsertion",
        () => this.recognizeCommand.execute(),
        { priority: "lowest" },
      );

    this.createBalloonMenu();
  }

  createLatexViewElement(element: Element, { writer }: DowncastConversionApi) {
    return toWidget(
      writer.createContainerElement(
        element.name === "latex-block" ? "div" : "span",
        { class: element.name },
      ),
      writer,
      { label: "Recognized LaTeX equation" },
    );
  }

  getSelectedEquationWidget(selection?: ViewDocumentSelection) {
    selection = selection ?? this.editor.editing.view.document.selection;
    const element = selection.getSelectedElement();
    if (
      !element?.hasClass("latex-inline") &&
      !element?.hasClass("latex-block")
    ) {
      return null;
    }
    return element;
  }

  convertLatex(to: "equation" | "plain") {
    const element = this.getSelectedEquationWidget();
    if (element == null) return;

    this.convertCommand.execute(element, to);
  }

  createBalloonMenu() {
    this.editor.ui.componentFactory.add("latexConvertButton", (locale) => {
      const convertButton = new ButtonView(this.editor.locale);
      convertButton.set({
        label: "Convert To Equation",
        withText: true,
        class: "ck-button-save",
        icon: IconCheck,
      });
      convertButton.on("execute", () => this.convertLatex("equation"));
      return convertButton;
    });

    this.editor.ui.componentFactory.add("latexDiscardButton", (locale) => {
      const discardButton = new ButtonView(this.editor.locale);
      discardButton.set({
        label: "Discard",
        withText: true,
        icon: IconEraser,
      });
      discardButton.on("execute", () => this.convertLatex("plain"));
      return discardButton;
    });

    this.editor.plugins.get(WidgetToolbarRepository).register("latex", {
      ariaLabel: this.editor.t("LaTeX equation toolbar"),
      items: ["latexConvertButton", "latexDiscardButton"],
      getRelatedElement: (selection) =>
        this.getSelectedEquationWidget(selection),
    });
  }
}
